import { Role } from "types/Role";
import { canManageOnboarding, HIRING_HEAD_PERMISSION } from "utils/onboardingPermissions";
import { normalizeJobLeaveStatus } from "utils/jobLeaveStatus";
import { vilniusTodayYmd } from "utils/vilnius-date";
import type {
  DateValue,
  PersonnelOnboardingRef,
  PersonnelProbation,
  PersonnelSummary,
} from "services/personnel.service";

/**
 * Pure helpers of the Personalas list and the «darbuotojo kortelė» — kept
 * out of the components so the visibility rules are unit-tested.
 */

/** Derived by the backend (auth/get-role.js) for heads of a department with data access. */
export const SUBORDINATES_PERMISSION = "Access personnel (subordinates)";

const WORK_SECURITY_PERMISSION = "Access work security assignments";

/** Who reaches Personalas → «Darbuotojai» (menu, routes). Mirrors Menu.js / App.tsx. */
export const PERSONNEL_PERMISSION_GROUPS = [
  "Master",
  "Access onboarding",
  WORK_SECURITY_PERMISSION,
  SUBORDINATES_PERMISSION,
];

/** Who reaches «Darbo sutartys» (the onboarding records list) — the /onboarding routes in App.tsx. */
export const ONBOARDING_LIST_GROUPS = ["Master", "Access onboarding", HIRING_HEAD_PERMISSION];

/** Who reaches «Pareigos» (the positions catalogue) — the /positions route. WS reads it only. */
export const POSITIONS_VIEW_GROUPS = ["Master", "Access onboarding", WORK_SECURITY_PERMISSION];

/** Who reaches «Skyriai» (the departments list and detail) — the /departments routes. WS reads them only. */
export const DEPARTMENTS_VIEW_GROUPS = ["Master", "Access departments", WORK_SECURITY_PERMISSION];

/**
 * Everyone who sees at least one Personalas tab (the menu item's visibility).
 * The positions groups are already part of PERSONNEL_PERMISSION_GROUPS.
 */
export const PERSONALAS_MENU_GROUPS = [
  ...PERSONNEL_PERMISSION_GROUPS,
  HIRING_HEAD_PERMISSION,
  "Access departments",
];

/** The WS module's signed-documents browser (work-security/users/:id/signed-documents). */
const SIGNED_DOCUMENTS_GROUPS = [
  "Master",
  WORK_SECURITY_PERMISSION,
  "Access work security module",
];

/** Who reaches the business-trips module's own screens (App.tsx `business-trips` routes). */
const BUSINESS_TRIP_MODULE_GROUPS = ["Master", "Access business trips"];

/** Who reaches the fuel-reports module's own screens (App.tsx `fuel-reports` routes). */
const FUEL_REPORT_MODULE_GROUPS = ["Master", "Access fuel reports"];

const hasAny = (role: Role | null | undefined, groups: string[]) =>
  groups.some((group) => !!role?.permissionsgroups?.includes(group));

export function canOpenPersonnel(role?: Role | null): boolean {
  return hasAny(role, PERSONNEL_PERMISSION_GROUPS);
}

/** Creates, edits, archives and merges positions — the catalogue CRUD is HR's, same rule as onboarding. */
export function canManagePositions(role?: Role | null): boolean {
  return canManageOnboarding(role);
}

/** Creates and edits departments and their subgroups — the backend's canAccessDepartments. */
export function canManageDepartments(role?: Role | null): boolean {
  return hasAny(role, ["Master", "Access departments"]);
}

export function canBrowseSignedDocuments(role?: Role | null): boolean {
  return hasAny(role, SIGNED_DOCUMENTS_GROUPS);
}

/**
 * A card row may link into the business-trips / fuel-reports module's own
 * detail screen only when the viewer holds that module's permission
 * (Master or the permission itself) — a head sees the tab (the backend's
 * `viewerCan`) but never the link, same as they never get the row's fileId.
 */
export function canOpenBusinessTrip(role?: Role | null): boolean {
  return hasAny(role, BUSINESS_TRIP_MODULE_GROUPS);
}

/** «Kuro ataskaitos» rows link the same way; «Išlaidos» shares this permission too (no separate one exists). */
export function canOpenFuelReport(role?: Role | null): boolean {
  return hasAny(role, FUEL_REPORT_MODULE_GROUPS);
}

export interface PersonalasTabsVisibility {
  employees: boolean;
  onboardings: boolean;
  positions: boolean;
  departments: boolean;
}

/**
 * «Darbuotojai | Darbo sutartys | Pareigos | Skyriai». A head who only holds the
 * derived subordinates permission sees «Darbuotojai» alone.
 */
export function personalasTabsFor(role?: Role | null): PersonalasTabsVisibility {
  return {
    employees: canOpenPersonnel(role),
    onboardings: hasAny(role, ONBOARDING_LIST_GROUPS),
    positions: hasAny(role, POSITIONS_VIEW_GROUPS),
    departments: hasAny(role, DEPARTMENTS_VIEW_GROUPS),
  };
}

/** Where the Personalas menu item leads: the first tab the viewer sees, in strip order. */
export function personalasHomePath(companyId: string, role?: Role | null): string {
  const tabs = personalasTabsFor(role);
  if (tabs.employees) return `/app/${companyId}/personnel`;
  if (tabs.onboardings) return `/app/${companyId}/onboarding`;
  if (tabs.positions) return `/app/${companyId}/positions`;
  if (tabs.departments) return `/app/${companyId}/departments`;
  return `/app/${companyId}/personnel`;
}

/**
 * Card tab ids. «leave» is «Išėjimas» (the job leave process); «leaves» is
 * «Atostogos» (leave requests) — named after the backend's `viewerCan` flags.
 */
export type CardTab =
  | "summary"
  | "contract"
  | "procedures"
  | "health"
  | "leaves"
  | "assets"
  | "businessTrips"
  | "fuelReports"
  | "expenses"
  | "leave"
  | "records"
  | "history";

export const CARD_TAB_ORDER: CardTab[] = [
  "summary",
  "contract",
  "procedures",
  "health",
  "leaves",
  "assets",
  "businessTrips",
  "fuelReports",
  "expenses",
  "leave",
  "records",
  "history",
];

/**
 * Tabs of the card the viewer may open, in display order. Each follows the
 * backend's `viewerCan`; «Išėjimas» also needs a job leave (with its id) and
 * «Sutartis ir pakeitimai» an onboarding record to show.
 */
export function visibleCardTabs(summary: Pick<PersonnelSummary, "viewerCan" | "jobLeave" | "onboardings">): CardTab[] {
  const can = summary.viewerCan;
  const visible: Record<CardTab, boolean> = {
    summary: true,
    contract: can.amendments && summary.onboardings.length > 0,
    procedures: can.workSecurity,
    health: can.medical,
    leaves: can.leaves,
    assets: can.assets,
    businessTrips: can.businessTrips,
    fuelReports: can.fuelReports,
    expenses: can.expenses,
    leave: can.jobLeave && !!summary.jobLeave?.id,
    records: can.manageOnboarding,
    history: can.audit,
  };
  return CARD_TAB_ORDER.filter((tab) => visible[tab]);
}

/** The requested `?tab=` when the viewer may see it, else the first tab. */
export function resolveCardTab(requested: string | null | undefined, visible: CardTab[]): CardTab {
  const match = visible.find((tab) => tab === requested);
  return match || visible[0] || "summary";
}

/**
 * The onboarding record the card's «new amendment» / «start leave» actions
 * open: the newest one that was not cancelled, else the newest. The list
 * comes newest first.
 */
export function primaryOnboarding(onboardings: PersonnelOnboardingRef[]): PersonnelOnboardingRef | null {
  return onboardings.find((item) => item.status !== "cancelled") || onboardings[0] || null;
}

/**
 * Whether the onboarding record view shows contract amendments for this
 * record: a contract registration always, a hire once it is past the
 * contract (work_security / step_5) or finished. Mirrors
 * onboarding-view-container.tsx.
 */
export function recordHasAmendments(record: Pick<PersonnelOnboardingRef, "mode" | "status">): boolean {
  if (record.mode === "contract_registration") return true;
  return ["work_security", "step_5", "completed", "cancelled"].includes(record.status || "");
}

/**
 * The record view locks a completed hire's amendments while it has any job
 * leave that was not cancelled (a finished leave too) — same rule here.
 */
export function recordLeaveLocks(jobLeaves: { status?: string | null }[]): boolean {
  return jobLeaves.some((row) => normalizeJobLeaveStatus((row.status || undefined) as any) !== "cancelled");
}

/**
 * The record «Sutartis ir pakeitimai» opens on: the one the current terms
 * come from (`employmentTerms.onboardingId`) when it is in the list, else
 * the newest not cancelled.
 */
export function defaultContractRecordId(
  summary: Pick<PersonnelSummary, "employmentTerms" | "onboardings">
): string | null {
  const source = summary.employmentTerms?.onboardingId;
  if (source && summary.onboardings.some((item) => item.id === source)) return source;
  return primaryOnboarding(summary.onboardings)?.id || null;
}

/**
 * Whether the summary's terms describe this record. Exact match on
 * `employmentTerms.onboardingId`; when the backend did not name a source
 * record, they are shown for the default record only.
 */
export function termsShownForRecord(
  summary: Pick<PersonnelSummary, "employmentTerms" | "onboardings">,
  recordId: string | null | undefined
): boolean {
  const terms = summary.employmentTerms;
  if (!terms || !recordId) return false;
  if (terms.onboardingId) return terms.onboardingId === recordId;
  return recordId === defaultContractRecordId(summary);
}

/** Prefill an amendment's salary / hours from the terms only for their own record (exact match). */
export function termsPrefillRecord(
  terms: Pick<NonNullable<PersonnelSummary["employmentTerms"]>, "onboardingId"> | null | undefined,
  recordId: string | null | undefined
): boolean {
  return !!terms?.onboardingId && !!recordId && terms.onboardingId === recordId;
}

/** A job leave that is still running (not completed, not cancelled). */
export function isLeaveActive(jobLeave: { status: string | null } | null | undefined): boolean {
  return !!jobLeave && jobLeave.status !== "completed" && jobLeave.status !== "cancelled";
}

/** Still employed here: not disabled and not marked as left. */
export function isEmployed(membership: Pick<PersonnelSummary["membership"], "isDisabled" | "employmentStatus">): boolean {
  return !membership.isDisabled && membership.employmentStatus !== "left";
}

/**
 * «Pradėti išėjimą»: needs HR reach and an onboarding record to open; hidden
 * while a leave is running or once the person has left. A completed or
 * cancelled leave of someone still employed does not hide it.
 */
export function canStartLeave(
  summary: Pick<PersonnelSummary, "viewerCan" | "jobLeave" | "membership" | "onboardings">
): boolean {
  return (
    summary.viewerCan.manageOnboarding &&
    !!primaryOnboarding(summary.onboardings) &&
    !isLeaveActive(summary.jobLeave) &&
    isEmployed(summary.membership)
  );
}

const pad = (n: number) => String(n).padStart(2, "0");

/** YYYY-MM-DD (Lithuanian date format) from a timestamp or a date string; "" when empty. */
export function formatDay(value: DateValue | undefined): string {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const iso = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (iso && value.length <= 10) return iso[1];
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return iso ? iso[1] : value;
    value = parsed.getTime();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export type MedicalState = "expired" | "expiring" | "valid";

/** Medical validity chip: expired, expiring within `soonDays`, or valid. null when unknown. */
export function medicalState(validUntil: DateValue | undefined, now: Date = new Date(), soonDays = 30): MedicalState | null {
  const day = formatDay(validUntil);
  if (!day) return null;
  const today = formatDay(now.getTime());
  if (day < today) return "expired";
  const soon = new Date(now.getTime() + soonDays * 24 * 60 * 60 * 1000);
  return day <= formatDay(soon.getTime()) ? "expiring" : "valid";
}

/** Card i18n key + interpolation params for the «Bandomasis laikotarpis» tile (docs/prd/probation-salary-review.md §7.4). */
export interface ProbationChip {
  key: string;
  params?: Record<string, string>;
}

/**
 * "Today" as the current Europe/Vilnius calendar date, UTC-midnight epoch ms
 * (docs/prd/probation-salary-review.md §1: never floor `Date.now()` to a UTC
 * day). Reuses `utils/vilnius-date`'s `vilniusTodayYmd` rather than
 * re-implementing the Intl.DateTimeFormat call.
 */
function vilniusTodayUtcMs(): number {
  const [y, m, d] = vilniusTodayYmd().split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

/**
 * The tile's status chip. Returns null for `cancelled`, which hides the
 * whole tile — every other status always shows something. The `pending`
 * split (still running vs. now awaiting a decision) is a display-only
 * re-derivation of the backend's `requestAt = end − 21 days` (§3): the
 * summary does not carry `requestAt` itself, and recomputing it from
 * `endDate` here is precise enough for a chip, never for an actual gate.
 * `now` defaults to today's Vilnius calendar date but stays injectable for
 * tests (and for the rare caller that already has "today" computed).
 */
export function probationChip(probation: PersonnelProbation, now: number = vilniusTodayUtcMs()): ProbationChip | null {
  const endDay = formatDay(probation.endDate);
  switch (probation.status) {
    case "cancelled":
      return null;
    case "passed":
      return { key: "probation_status_passed" };
    case "passed_unreviewed":
      return { key: "probation_status_passed_unreviewed" };
    case "failed":
      return { key: "probation_status_failed", params: { deadline: formatDay(probation.noticeDeadline) } };
    case "pending":
    default: {
      const endMs = typeof probation.endDate === "number" ? probation.endDate : Date.parse(String(probation.endDate));
      const requestAtMs = Number.isFinite(endMs) ? endMs - 21 * 24 * 60 * 60 * 1000 : null;
      if (requestAtMs != null && now >= requestAtMs) {
        return { key: "probation_status_awaiting_review", params: { deadline: formatDay(probation.decisionDeadline) } };
      }
      return { key: "probation_status_ongoing", params: { endDate: endDay } };
    }
  }
}

interface DepartmentLike {
  id: string;
  name: string;
  subgroups?: { id: string; name: string }[];
}

/** Department filter options: top-level departments with their sub-departments right after them. */
export function flattenDepartments(departments: DepartmentLike[]): { id: string; name: string }[] {
  const out: { id: string; name: string }[] = [];
  for (const department of departments) {
    out.push({ id: department.id, name: department.name });
    for (const sub of department.subgroups || []) {
      out.push({ id: sub.id, name: `${department.name} › ${sub.name}` });
    }
  }
  return out;
}

/** Departments seen on list rows — the filter's fallback when the viewer cannot read the departments list. */
export function departmentsFromRows(
  rows: { departments: { id: string; name: string }[] }[],
  known: { id: string; name: string }[] = []
): { id: string; name: string }[] {
  const byId = new Map(known.map((item) => [item.id, item]));
  for (const row of rows) {
    for (const department of row.departments) {
      if (department.id && !byId.has(department.id)) byId.set(department.id, department);
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, "lt"));
}

/** i18n key of a contract type — the same keys as the onboarding record view. */
export function contractTypeKey(type: string | null | undefined): string | null {
  if (!type) return null;
  return `contract_type_${type === "other" ? "other_value" : type}`;
}

/**
 * Audit subjects shown in the card's «Istorija». Positions entries are keyed
 * by the catalogue position, not by the person, so they are not included.
 */
export const CARD_AUDIT_SUBJECT_TYPES = "User,UserMembership,Onboarding";

/**
 * subjectId CSV for «Istorija»: the user (User), their membership in this
 * company (UserMembership) and each of their onboarding records (Onboarding).
 * Every id must belong to the company — the backend refuses the whole request
 * otherwise — so only ids taken from the summary go in.
 */
export function cardAuditSubjectIds(
  summary: Pick<PersonnelSummary, "membership" | "onboardings">,
  userId: string
): string {
  const ids = [userId, summary.membership.companiesUserId, ...summary.onboardings.map((item) => item.id)];
  return Array.from(new Set(ids.filter((id): id is string => !!id))).join(",");
}
